import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { getTodayEastern } from '../../lib/dateHelpers'

export default function CrewSelection({ crewLeaderId, compact, onSaved }) {
  const [workers, setWorkers] = useState([])
  const [schedIds, setSchedIds] = useState(new Set())
  const [clockedInIds, setClockedInIds] = useState(new Set())
  const [selected, setSelected] = useState(new Set())
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(true)

  const today = getTodayEastern()

  const load = useCallback(async () => {
    setLoading(true)
    const [wRes, sRes, aRes, eRes] = await Promise.all([
      supabase.from('profiles').select('*').eq('is_active', true).in('role', ['crew_leader', 'laborer', 'project_manager']).order('full_name'),
      supabase.from('worker_schedule').select('user_id, status').eq('schedule_date', today),
      supabase.from('daily_attendance').select('user_id').eq('work_date', today).is('clock_out', null),
      supabase.from('daily_crew_assignments').select('worker_id').eq('crew_leader_id', crewLeaderId).eq('work_date', today),
    ])
    setWorkers(wRes.data || [])
    setSchedIds(new Set((sRes.data || []).filter(s => s.status === 'scheduled').map(s => s.user_id)))
    setClockedInIds(new Set((aRes.data || []).map(a => a.user_id)))
    setSelected(new Set((eRes.data || []).map(r => r.worker_id)))
    setLoading(false)
  }, [crewLeaderId, today])

  useEffect(() => { load() }, [load])

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
    setSaved(false)
  }

  const selectAllClockedIn = () => {
    setSelected(prev => {
      const next = new Set(prev)
      clockedInIds.forEach(id => { if (id !== crewLeaderId) next.add(id) })
      return next
    })
    setSaved(false)
  }

  const clearAll = () => { setSelected(new Set()); setSaved(false) }

  const saveCrew = async () => {
    setSaving(true)
    await supabase.from('daily_crew_assignments').delete().eq('crew_leader_id', crewLeaderId).eq('work_date', today)
    const ids = Array.from(selected)
    if (ids.length > 0) {
      await supabase.from('daily_crew_assignments').insert(ids.map(wid => ({ work_date: today, crew_leader_id: crewLeaderId, worker_id: wid })))
    }
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
    if (onSaved) onSaved(ids)
  }

  const tagFor = (w) => {
    if (clockedInIds.has(w.id)) return { label: 'CLOCKED IN', color: '#4a8c6a', priority: 0 }
    if (schedIds.has(w.id)) return { label: 'SCHEDULED', color: '#b48c3c', priority: 1 }
    if (w.availability_status === 'available') return { label: 'AVAILABLE', color: 'rgba(255,255,255,0.5)', priority: 2 }
    return { label: 'OFF SEASON', color: 'rgba(255,255,255,0.3)', priority: 3 }
  }

  const sorted = workers
    .filter(w => w.id !== crewLeaderId)
    .map(w => ({ w, t: tagFor(w) }))
    .sort((a, b) => {
      if (a.t.priority !== b.t.priority) return a.t.priority - b.t.priority
      return (a.w.full_name || '').localeCompare(b.w.full_name || '')
    })

  const clockedInCount = Array.from(clockedInIds).filter(id => id !== crewLeaderId).length

  return (
    <div style={{ ...s.wrapper, ...(compact ? s.wrapperCompact : {}) }}>
      <div style={s.header}>
        <div style={s.title}>SELECT TODAY'S CREW</div>
        <button disabled={saving || loading} onClick={saveCrew} style={{ ...s.saveBtn, backgroundColor: saved ? '#4a8c6a' : '#b48c3c' }}>
          {saving ? 'SAVING...' : saved ? 'SAVED!' : `SAVE CREW (${selected.size})`}
        </button>
      </div>
      <div style={s.quickRow}>
        <button onClick={selectAllClockedIn} disabled={clockedInCount === 0} style={{ ...s.quickBtn, opacity: clockedInCount === 0 ? 0.4 : 1 }}>
          ✓ SELECT ALL CLOCKED IN ({clockedInCount})
        </button>
        {selected.size > 0 && <button onClick={clearAll} style={s.quickBtn}>CLEAR</button>}
      </div>
      {loading ? (
        <div style={s.empty}>Loading workers...</div>
      ) : sorted.length === 0 ? (
        <div style={s.empty}>No workers found.</div>
      ) : (
        <div style={s.list}>
          {sorted.map(({ w, t }) => {
            const isSelected = selected.has(w.id)
            const clockedIn = clockedInIds.has(w.id)
            return (
              <div key={w.id} onClick={() => toggle(w.id)} style={{ ...s.row, borderColor: isSelected ? 'rgba(74,140,106,0.4)' : 'rgba(255,255,255,0.06)', backgroundColor: isSelected ? 'rgba(74,140,106,0.08)' : 'rgba(255,255,255,0.02)' }}>
                <div style={{ ...s.check, backgroundColor: isSelected ? '#4a8c6a' : 'transparent', borderColor: isSelected ? '#4a8c6a' : 'rgba(255,255,255,0.2)' }}>
                  {isSelected && <span style={{ color: '#fff', fontSize: '12px' }}>&#10003;</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '15px', fontWeight: 500, color: '#ffffff' }}>{w.full_name}</span>
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginLeft: '8px' }}>{w.role?.replace('_', ' ')}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  {clockedIn && <span title="Clocked in today" style={{ width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#4a8c6a', display: 'inline-block', boxShadow: '0 0 0 3px rgba(74,140,106,0.2)' }} />}
                  <span style={{ fontSize: '10px', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1px', color: t.color, border: `1px solid ${t.color}40`, borderRadius: '2px', padding: '2px 6px' }}>{t.label}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const s = {
  wrapper: { marginBottom: '20px' },
  wrapperCompact: { marginBottom: 0 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '10px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 600, letterSpacing: '2px', color: '#b48c3c' },
  saveBtn: { padding: '8px 16px', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  quickRow: { display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' },
  quickBtn: { padding: '6px 12px', backgroundColor: 'rgba(180,140,60,0.08)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.2px', cursor: 'pointer' },
  list: { display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '60vh', overflowY: 'auto' },
  row: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', border: '1px solid', borderRadius: '4px', cursor: 'pointer' },
  check: { width: '22px', height: '22px', borderRadius: '4px', border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  empty: { padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
}
